import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import type { NarrowedEntityData } from '@adornis/bh-util/selectionset-types.js';
import { baseConfig } from '@adornis/config/baseConfig.js';
import type { Message } from '@adornis/whatsapp/server/types.js';
import {
  ComponentParamaterType,
  ComponentSubType,
  ComponentType,
  MessageType,
} from '@adornis/whatsapp/server/types.js';
import type { Appointment } from '../entities/appointment.js';
import { getInstitutionWhatsappConfigRaw, type WhatsappDeliveryMode } from '../entities/institution-whatsapp-config.js';
import {
  getWhatsappBodyParamCount,
  getWhatsappTemplate,
  type GlobalMailTemplate,
  MailTemplateType,
  MailTemplateVars,
  type WhatsAppTemplate,
} from '../entities/mail-templates.js';
import {
  NotificationSendLog,
  type NotificationSendLogChannel,
  type NotificationSendLogStatus,
} from '../entities/notification-send-log.js';
import type { User } from '../entities/user.js';
import { mongo } from '../server/mongo.js';
import { getWhatsappClientForInstitution, isWhatsappConfiguredForInstitution } from '../whatsapp/util.js';

export function toWhatsappBodyParam(text: string | null | undefined): string {
  return (
    text
      ?.replace(/<[^>]*>/g, '')
      .replace(/\s+/g, ' ')
      .trim() || '-'
  );
}

export function getWhatsappDeploymentSuffix(deployment: string | null | undefined): string {
  if (!deployment || deployment === 'production') return '';
  const readable: Record<string, string> = {
    staging: ' (Staging)',
    qa: ' (QA)',
    qa2: ' (QA2)',
    testing: ' (Testing)',
    development: ' (Dev)',
  };
  return readable[deployment] ?? ` (${deployment})`;
}

export function applyWhatsappDeploymentSuffix(
  template: WhatsAppTemplate,
  parameters: string[],
  deployment: string | null | undefined,
): string[] {
  const suffix = getWhatsappDeploymentSuffix(deployment);
  return parameters.map((parameter, index) => {
    const label = template.bodyParams?.[index];
    return label === 'Produktname' || label === 'Produktnamen' ? parameter + suffix : parameter;
  });
}

export function shouldSendWhatsappNotification(
  user: NarrowedEntityData<User, { whatsappNotification: 1; whatsappPhone: 1 }>,
  mailTemplate: NarrowedEntity<GlobalMailTemplate, { whatsappNotification: 1 }>,
) {
  return user && user.whatsappNotification && user.whatsappPhone && mailTemplate.whatsappNotification;
}

function shoudSendEmailNotification(
  user: NarrowedEntityData<User, { email: 1 }>,
  mailTemplate: NarrowedEntity<GlobalMailTemplate, { emailNotification: 1 }>,
) {
  return user && user.email && mailTemplate.emailNotification;
}

export async function getNotificationType(
  user: NarrowedEntityData<User, { _id: 1; email: 1; whatsappNotification: 1; whatsappPhone: 1 }>,
  mailTemplate: NarrowedEntity<GlobalMailTemplate, { emailType: 1; emailNotification: 1; whatsappNotification: 1 }>,
  institutionID?: string | null,
): Promise<'whatsapp' | 'email' | 'none'> {
  const warnIfEmailCannotBeDelivered = () => {
    const context = { userID: user._id, institutionID, emailType: mailTemplate.emailType };
    if (!user.email) {
      logger.warn(context, 'notification dropped: whatsapp unavailable and user has no email address');
    }
    if (mailTemplate.emailNotification === false) {
      logger.warn(
        context,
        'notification dropped: whatsapp unavailable and email notification disabled for this template type',
      );
    }
  };

  const getEmailNotificationType = (): 'email' | 'none' => {
    if (shoudSendEmailNotification(user, mailTemplate)) return 'email';
    warnIfEmailCannotBeDelivered();
    return 'none';
  };

  if (shouldSendWhatsappNotification(user, mailTemplate)) {
    try {
      getWhatsappTemplate(mailTemplate.emailType as MailTemplateType);
    } catch (error) {
      if (error instanceof Error && error.message === 'not implemented') {
        return getEmailNotificationType();
      }
      throw error;
    }

    if (institutionID && (await isWhatsappConfiguredForInstitution(institutionID))) return 'whatsapp';
    return getEmailNotificationType();
  }

  return getEmailNotificationType();
}

export async function writeNotificationSendLog(args: {
  institutionID: string;
  userID: string;
  mailTemplateType: MailTemplateType;
  channel?: NotificationSendLogChannel;
  status: NotificationSendLogStatus;
  errorMessage?: string;
  waMessageID?: string;
}) {
  const collection = mongo().collection<EntityData<NotificationSendLog>>(NotificationSendLog._collectionName);
  await collection.insertOne({
    _id: A.getGloballyUniqueID(),
    _class: NotificationSendLog._class,
    channel: args.channel ?? 'whatsapp',
    institutionID: args.institutionID,
    userID: args.userID,
    mailTemplateType: args.mailTemplateType,
    status: args.status,
    errorMessage: args.errorMessage ?? null,
    waMessageID: args.waMessageID ?? null,
    timestamp: new Date(),
  });
}

export const sendWhatsappTemplateMessage = async (args: {
  institutionID: string;
  user: { _id: string; whatsappPhone: string };
  mailTemplateType: MailTemplateType;
  parameters: string[];
  buttonUrlParameters?: {
    index: string;
    parameters: { type: ComponentParamaterType.TEXT; text: string }[];
  };
  headerParameters?: (
    | { type: ComponentParamaterType.DOCUMENT; document: { link: string } }
    | { type: ComponentParamaterType.IMAGE; image: { link: string } }
  )[];
}): Promise<{ ok: boolean; messageID?: string; error?: string }> => {
  const whatsapp = await getWhatsappClientForInstitution(args.institutionID);
  if (!whatsapp) {
    logger.warn(
      { institutionID: args.institutionID, mailTemplateType: args.mailTemplateType },
      'whatsapp template message not sent because whatsapp is not configured',
    );
    return { ok: false, error: 'not configured' };
  }

  let messageID: string | undefined;
  try {
    const template = getWhatsappTemplate(args.mailTemplateType);
    const parameters = applyWhatsappDeploymentSuffix(template, args.parameters, baseConfig.get('DEPLOYMENT'));
    const bodyParamCount = getWhatsappBodyParamCount(template);
    if (bodyParamCount !== undefined && bodyParamCount !== args.parameters.length) {
      logger.warn(
        {
          templateName: template.name,
          expectedBodyParamCount: bodyParamCount,
          actualBodyParamCount: args.parameters.length,
        },
        'whatsapp template body parameter count differs from the configured template metadata',
      );
    }

    const message: Message = {
      to: args.user.whatsappPhone,
      type: MessageType.TEMPLATE,
      content: {
        template: {
          name: template.name,
          language: { code: 'de' },
          components: [
            {
              type: ComponentType.BODY,
              parameters: parameters.map(text => ({ type: ComponentParamaterType.TEXT, text })),
            },
            ...(args.buttonUrlParameters
              ? [
                  {
                    type: ComponentType.BUTTON,
                    sub_type: ComponentSubType.URL,
                    index: args.buttonUrlParameters.index,
                    parameters: args.buttonUrlParameters.parameters,
                  },
                ]
              : []),
            ...(args.headerParameters
              ? [
                  {
                    type: ComponentType.HEADER,
                    parameters: args.headerParameters,
                  },
                ]
              : []),
          ],
        },
      },
    };

    const result = await whatsapp.sendMessage(message);
    messageID = result.messages?.[0]?.id;
  } catch (err) {
    const error = err instanceof Error ? err.message : String(err);
    try {
      await writeNotificationSendLog({
        institutionID: args.institutionID,
        userID: args.user._id,
        mailTemplateType: args.mailTemplateType,
        status: 'failed',
        errorMessage: error,
      });
    } catch (logError) {
      logger.error(
        {
          err: logError,
          institutionID: args.institutionID,
          userID: args.user._id,
          mailTemplateType: args.mailTemplateType,
        },
        'failed to write whatsapp notification send log',
      );
    }
    logger.error(
      { err, institutionID: args.institutionID, userID: args.user._id, mailTemplateType: args.mailTemplateType },
      'whatsapp template message failed',
    );
    return { ok: false, error };
  }

  try {
    await writeNotificationSendLog({
      institutionID: args.institutionID,
      userID: args.user._id,
      mailTemplateType: args.mailTemplateType,
      status: 'sent',
      waMessageID: messageID,
    });
  } catch (logError) {
    logger.error(
      {
        err: logError,
        institutionID: args.institutionID,
        userID: args.user._id,
        mailTemplateType: args.mailTemplateType,
      },
      'failed to write whatsapp notification send log',
    );
  }
  logger.info(
    { institutionID: args.institutionID, userID: args.user._id, mailTemplateType: args.mailTemplateType, messageID },
    'whatsapp template message sent',
  );
  return { ok: true, ...(messageID ? { messageID } : {}) };
};

type WhatsappTemplateMessageArgs = Parameters<typeof sendWhatsappTemplateMessage>[0];

export async function sendWhatsappWithEmailFallback(
  args: Omit<WhatsappTemplateMessageArgs, 'user'> & {
    user: WhatsappTemplateMessageArgs['user'] & { email?: string | null };
    mailTemplate: NarrowedEntity<GlobalMailTemplate, { emailNotification: 1 }>;
    sendEmail: () => Promise<unknown>;
  },
  {
    sendWhatsapp = sendWhatsappTemplateMessage,
    getDeliveryMode = async institutionID =>
      (await getInstitutionWhatsappConfigRaw(institutionID))?.deliveryMode ?? 'fallback',
    writeLog = writeNotificationSendLog,
  }: {
    sendWhatsapp?: typeof sendWhatsappTemplateMessage;
    getDeliveryMode?: (institutionID: string) => Promise<WhatsappDeliveryMode>;
    writeLog?: typeof writeNotificationSendLog;
  } = {},
): Promise<{ ok: boolean; channel: 'whatsapp' | 'email-fallback' | 'email' | 'both' | 'none'; error?: string }> {
  const { mailTemplate, sendEmail, ...whatsappArgs } = args;
  const deliveryMode = await getDeliveryMode(args.institutionID);
  if (deliveryMode === 'both') {
    let emailOk = false;
    let emailError: string | undefined;
    if (mailTemplate.emailNotification && args.user.email) {
      try {
        emailOk = (await sendEmail()) !== false;
        if (!emailOk) emailError = 'email send returned false';
      } catch (error) {
        emailError = error instanceof Error ? error.message : String(error);
      }
      try {
        await writeLog({
          institutionID: args.institutionID,
          userID: args.user._id,
          mailTemplateType: args.mailTemplateType,
          channel: 'email',
          status: emailOk ? 'sent' : 'failed',
          errorMessage: emailError,
        });
      } catch (logError) {
        logger.error(
          {
            err: logError,
            institutionID: args.institutionID,
            userID: args.user._id,
            mailTemplateType: args.mailTemplateType,
          },
          'failed to write email notification send log',
        );
      }
    }

    // A failure on the second channel must not trigger a retry of an already delivered email.
    let result: Awaited<ReturnType<typeof sendWhatsappTemplateMessage>>;
    try {
      result = await sendWhatsapp(whatsappArgs);
    } catch (error) {
      result = { ok: false, error: error instanceof Error ? error.message : String(error) };
    }
    const ok = emailOk || result.ok;
    return {
      ok,
      channel: emailOk ? (result.ok ? 'both' : 'email') : result.ok ? 'whatsapp' : 'none',
      ...(ok ? {} : { error: `whatsapp failed: ${result.error ?? 'unknown'}; email: ${emailError ?? 'unavailable'}` }),
    };
  }

  const result = await sendWhatsapp(whatsappArgs);
  if (result.ok) return { ok: true, channel: 'whatsapp' };

  const logFallback = async (status: NotificationSendLogStatus, errorMessage?: string) => {
    try {
      await writeLog({
        institutionID: args.institutionID,
        userID: args.user._id,
        mailTemplateType: args.mailTemplateType,
        channel: 'email-fallback',
        status,
        errorMessage,
      });
    } catch (logError) {
      logger.error(
        {
          err: logError,
          institutionID: args.institutionID,
          userID: args.user._id,
          mailTemplateType: args.mailTemplateType,
        },
        'failed to write email fallback notification send log',
      );
    }
  };

  if (mailTemplate.emailNotification && args.user.email) {
    try {
      await sendEmail();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      await logFallback('failed', errorMessage);
      throw error;
    }
    await logFallback('sent');
    logger.info(
      {
        institutionID: args.institutionID,
        userID: args.user._id,
        mailTemplateType: args.mailTemplateType,
      },
      'notification delivered by email fallback after whatsapp failure',
    );
    return { ok: true, channel: 'email-fallback' };
  }

  const error = [
    `whatsapp failed: ${result.error ?? 'unknown'}`,
    !mailTemplate.emailNotification ? 'email notification disabled' : undefined,
    !args.user.email ? 'user has no email address' : undefined,
  ]
    .filter(Boolean)
    .join('; ');
  logger.error(
    {
      institutionID: args.institutionID,
      userID: args.user._id,
      mailTemplateType: args.mailTemplateType,
      whatsappError: result.error,
    },
    'notification dropped after whatsapp failure because email fallback is unavailable',
  );
  await logFallback('dropped', error);
  return { ok: false, channel: 'none', error };
}

// render

export function templateUsesMeetingCodeOrPassword(
  template: Maybe<{ subject?: unknown; content?: unknown } & Record<string, unknown>>,
): boolean {
  const containsVariable = (value: unknown): boolean => {
    if (typeof value === 'string')
      return [MailTemplateVars.MEETING_CODE, MailTemplateVars.MEETING_PASSWORD].some(variable =>
        value.includes(`{{${variable}}}`),
      );
    if (value && typeof value === 'object') return Object.values(value).some(containsVariable);
    return false;
  };
  return containsVariable(template?.subject) || containsVariable(template?.content);
}

export function getMeetingLinkHtml(
  appointment: NarrowedEntityData<Appointment, { meetingLink: { link: 1; code: 1; password: 1 } }>,
  options?: { omitCodeAndPassword?: boolean },
) {
  if (!appointment || !appointment.meetingLink) return '';

  let meetingLink = '';
  if (appointment.meetingLink?.link) {
    const ml = appointment.meetingLink;
    meetingLink = `<a href="${ml.link}">${ml.link}</a>`;
    if (!options?.omitCodeAndPassword) {
      if (ml.code) meetingLink += ` Code: ${ml.code}`;
      if (ml.password) meetingLink += ` Passwort: ${ml.password}`;
    }
  }

  return meetingLink;
}
